'use client';

import {
  bucketByCategory,
  VIDEO_CATEGORIES,
  type VideoCategory,
} from '@kit/artists';

import { VideoTile } from '~/components/video-tile';
import { DancerSection } from './dancer-section';
import type { DancerMediaItem } from './types';

interface Props {
  videos: DancerMediaItem[];
  /** Eyebrow override. Defaults to "Reels" to preserve legacy single-block
   * behaviour. Use "Watch" for the proof-first lead reel section, "More
   * footage" for the secondary clips section. */
  eyebrow?: string;
  /** Section title override. Defaults to "Selected video". */
  title?: string;
  /** Skip category bucketing when true — renders one flat grid. Used by
   * any caller that already pre-filtered to a single category or wants
   * the legacy single-block render. Defaults to false (bucket by
   * category). */
  flat?: boolean;
  /** When true, render only the video grid(s) without the outer
   * `DancerSection` chrome. Used by `DancerAdditionalMedia` (§13). */
  bare?: boolean;
}

/**
 * Selected reels — 16:9 grid of `VideoTile`s (the canonical editorial
 * click-to-play card, see `~/components/video-tile`), bucketed by
 * editorial category.
 *
 * Section order (spec 2026-05-16, proof-first):
 *   1. Performance
 *   2. Audition
 *   3. Choreography
 *   4. Commercial
 *   5. Improvisation
 *   6. Other (legacy Meteor values + uncategorised)
 *
 * Empty sections silently omit. When the caller passes `flat`, all
 * videos render in one grid without category headers (legacy
 * behaviour, used by the lead-reel single-video block).
 */
export function DancerVideos({
  videos,
  eyebrow = 'Reels',
  title = 'Selected video',
  flat = false,
  bare = false,
}: Props) {
  if (videos.length === 0) return null;

  if (flat) {
    if (bare) return <VideoGrid items={videos} />;
    return (
      <DancerSection eyebrow={eyebrow} title={title} count={videos.length}>
        <VideoGrid items={videos} />
      </DancerSection>
    );
  }

  const buckets = bucketByCategory(
    videos,
    VIDEO_CATEGORIES.map((c) => c.value),
  );
  const orderedSections: Array<{
    key: VideoCategory | 'other';
    label: string;
    items: DancerMediaItem[];
  }> = [
    ...VIDEO_CATEGORIES.map((c) => ({
      key: c.value as VideoCategory,
      label: c.label,
      items: buckets.get(c.value) ?? [],
    })),
    { key: 'other' as const, label: 'Other', items: buckets.get(null) ?? [] },
  ];

  const nonEmpty = orderedSections.filter((s) => s.items.length > 0);
  if (nonEmpty.length === 0) return null;

  const grids = (
    <div className="space-y-12">
      {nonEmpty.map((section) => (
        <div key={section.key} className="space-y-4">
          {nonEmpty.length > 1 && (
            <h3 className="text-foreground/60 font-mono text-[10px] tracking-[0.22em] uppercase">
              {section.label}
            </h3>
          )}
          <VideoGrid items={section.items} />
        </div>
      ))}
    </div>
  );

  if (bare) return grids;

  return (
    <DancerSection eyebrow={eyebrow} title={title} count={videos.length}>
      {grids}
    </DancerSection>
  );
}

function VideoGrid({ items }: { items: DancerMediaItem[] }) {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
      {items.map((video) => (
        <li key={video.id}>
          <VideoTile
            externalUrl={video.external_url}
            uploadedUrl={video.signed_url}
            thumbnailUrl={video.thumbnail_url}
            title={video.file_name}
            mediaId={video.id}
            muxPlaybackId={video.mux_playback_id}
            muxStatus={video.mux_status}
          />
        </li>
      ))}
    </ul>
  );
}
